import type { MapLocation } from "@/Shared/Maps/MapLocation";
import type { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import mapboxgl from "mapbox-gl";
import React, { useEffect, useRef, useState } from "react";
import { loadImages } from "./Helpers/images";
import { useRenderPopups } from "./Helpers/renderPopups";
import { setDeliveryLocation, setPickupLocation } from "./Helpers/setLocations";
import { setupArc } from "./Helpers/setupArc";
import { setupMarker } from "./Helpers/setupMarker";
import { zoomMap } from "./Helpers/zoomMap";
import { MAPBOX_ACCESS_TOKEN } from "@/Shared/Maps/mapboxAccessToken";
import { mapboxProjection } from "@freightsimple/ui-core/Display/mapboxProjection";
import { mapboxStyleUrl } from "@freightsimple/ui-core/Display/mapboxStyle";
import { useThemeContext } from "@freightsimple/ui-core/Theme/useThemeContext";
import { MapsFullBleedMapCanvas } from "./CustomComponents/MapsFullBleedMapCanvas";
import { useMapsResizeWithContainer } from "./Hooks/useMapsResizeWithContainer";

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

interface ShipmentMapProps {
  pickup: Partial<MapLocation>;
  delivery: Partial<MapLocation>;

  shipmentState: ShipmentState;
}

export function ShipmentMap(props: ShipmentMapProps) {
  const [map, setMap] = useState<mapboxgl.Map | undefined>();
  const mapContainer = useRef<HTMLDivElement>(null);
  const requestRef = React.useRef<number | undefined>(undefined);
  const [mapLoaded, setMapLoaded] = useState(false);
  useMapsResizeWithContainer(map, mapContainer);
  // The style-not-loaded retry below reschedules itself on a timer. Both of
  // these let the unmount cleanup stop it: without them a retry pending at
  // unmount fires against a map that has just been removed, which either logs
  // "Ran out of retries" at error level for someone simply navigating away, or
  // — if it lands before the deferred remove() — starts a fresh
  // requestAnimationFrame loop that nothing is left to cancel.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const unmountedRef = useRef(false);

  const offset = 0; // useDynamicHeight(0, 40);

  const { resolvedTheme } = useThemeContext();
  const isDark = resolvedTheme === "dark";

  const renderPopups = useRenderPopups();

  function setupLocations(
    map: mapboxgl.Map,
    pickup: Partial<MapLocation>,
    delivery: Partial<MapLocation>,
    shipmentState: ShipmentState,
  ) {
    setPickupLocation(map, pickup, shipmentState);
    setDeliveryLocation(map, delivery, shipmentState);

    renderPopups(map, pickup, delivery);

    setupArc(map, pickup, delivery, shipmentState);

    zoomMap(map, pickup, delivery);

    setupMarker(map, pickup, delivery, shipmentState, requestRef);
  }

  async function setupLocationsWrapper(map: mapboxgl.Map) {
    let remaining = 3;

    // We sometimes get exceptions that the style is not loaded. I can't find a way to wait for mapbox to properly have styles loaded
    // So let's just catch and retry to work around it
    function retry(f: () => void) {
      if (unmountedRef.current) {
        return;
      }
      try {
        f();
      } catch {
        remaining--;
        if (unmountedRef.current) {
          // Navigating away isn't a failure, so don't retry and don't log.
          return;
        }
        if (remaining > 0) {
          retryTimerRef.current = setTimeout(function () {
            retry(f);
          }, 500);
        } else {
          console.error("Ran out of retries");
        }
      }
    }

    retry(function () {
      setupLocations(map, props.pickup, props.delivery, props.shipmentState);
    });
  }

  useEffect(() => {
    // A Mapbox map owns a WebGL context and keeps fetching tiles and marker
    // images for as long as it lives, and it does not stop when React drops the
    // element it drew into. Without the teardown below every mount leaked one —
    // and the booking flow mounts this repeatedly — so the tile and pin requests
    // from abandoned maps pile up until the page never goes quiet. Browsers also
    // cap live WebGL contexts, so the leak eventually starts killing maps at
    // random. `created` rather than the `map` state because the teardown has to
    // work when unmount beats the async setup below.
    let created: mapboxgl.Map | undefined;
    let cancelled = false;
    let setupFinished: Promise<void> | undefined;

    async function initializeMap() {
      const currentMapContainer = mapContainer.current;
      if (!currentMapContainer) {
        return;
      }

      try {
        const mapToInitialize = new mapboxgl.Map({
          container: currentMapContainer,
          style: mapboxStyleUrl(isDark),
          center: [-90, 44],
          zoom: 3,
          // Globe where there's a GPU, flat where there isn't — see
          // mapboxProjection in ui-core. Pinned either way rather than left to
          // the style, which is how light mode once ended up a rectangle while
          // dark mode was a globe.
          projection: mapboxProjection(),
        });
        created = mapToInitialize;

        // Turn off all interactivity
        mapToInitialize.boxZoom.disable();
        mapToInitialize.scrollZoom.disable();
        mapToInitialize.dragRotate.disable();
        mapToInitialize.dragPan.disable();
        mapToInitialize.keyboard.disable();
        mapToInitialize.doubleClickZoom.disable();
        mapToInitialize.touchZoomRotate.disable();

        mapToInitialize.resize();

        await loadImages(mapToInitialize);

        if (cancelled) {
          return;
        }

        setMapLoaded(true);
        setMap(mapToInitialize);
      } catch (e) {
        console.warn(`Exception initializing map`, { e });
      }
    }

    if (map === undefined) {
      setupFinished = initializeMap();
    }

    return function () {
      cancelled = true;
      // Wait for the setup above to settle before tearing the map down.
      // `remove()` terminates Mapbox's web workers, and an image load still in
      // flight then posts to a worker that is gone — an unhandled rejection
      // ("Cannot read properties of null (reading 'postMessage')") that fails
      // whatever test is running and would show up in Sentry in production.
      void Promise.resolve(setupFinished).then(function () {
        created?.remove();
      });
    };
  }, []);

  useEffect(() => {
    return function () {
      // Flag first: it stops the style retry rescheduling itself, so clearing
      // the timer below can't be beaten by a retry that fires in between.
      unmountedRef.current = true;
      clearTimeout(retryTimerRef.current);
      const currentRequestRef = requestRef.current;
      if (currentRequestRef) {
        cancelAnimationFrame(currentRequestRef);
      }
    };
  }, []);

  // Switching theme swaps the Mapbox style, and a style swap drops every
  // source, layer, marker and custom image the setup added — so re-add them
  // once the new style is in. The ref holds the style the map was built with,
  // so this skips the first run rather than rebuilding a map that just loaded.
  const styleIsDarkRef = useRef(isDark);
  useEffect(() => {
    if (map === undefined || styleIsDarkRef.current === isDark) {
      return;
    }
    styleIsDarkRef.current = isDark;
    map.setStyle(mapboxStyleUrl(isDark));
    map.once("style.load", async function () {
      await loadImages(map);
      setupLocationsWrapper(map);
    });
  }, [isDark, map]);

  useEffect(() => {
    if (map === undefined) {
      return;
    }

    if (!mapLoaded) {
      map.on("load", function () {
        setupLocationsWrapper(map);
        setMapLoaded(true);
      });
    } else {
      setupLocationsWrapper(map);
    }
  }, [
    props.pickup.latitudeLongitude?.latitude,
    props.pickup.latitudeLongitude?.longitude,
    props.delivery.latitudeLongitude?.latitude,
    props.delivery.latitudeLongitude?.longitude,
    props.shipmentState,
    map,
  ]);

  function showNotActualRoute() {
    if (
      props.pickup.latitudeLongitude?.latitude &&
      props.delivery.latitudeLongitude?.latitude
    ) {
      return true;
    } else {
      return false;
    }
  }

  return (
    <MapsFullBleedMapCanvas
      containerRef={mapContainer}
      offsetPx={offset}
      showRouteDisclaimer={showNotActualRoute()}
    />
  );
}
